import { getMembershipPlanDisplayPricing } from '@frontend/api-client';
import { dateFormatters, formatCurrencyNatural, makeCent } from '@frontend/duck-tape';
import { getPlanSubtitle, usePaywallContext } from '@frontend/react-domain';
import { CardContainer, Divider, Icon, Text, XStack, YStack } from '@frontend/web-react';
import { usePricingTerminology } from '@frontend/web/context';

const PaymentSuccessLineItem = ({ label, value }: { label: string; value: string }) => {
  return (
    <XStack alignItems="center" justifyContent="between" py="sm">
      <Text type="p-sm">{label}</Text>
      <Text color="primary" type="p-sm">
        {value}
      </Text>
    </XStack>
  );
};

export const SuccessStep = () => {
  const { paywallState } = usePaywallContext();
  const { pricingTerminology, showCredits } = usePricingTerminology();

  const firstPurchaseData = paywallState.purchase;
  const promotionTranslation = paywallState.promotionTranslation;
  const selectedPlanPricing = getMembershipPlanDisplayPricing(firstPurchaseData.selectedPlanData);

  return (
    <YStack gapY="lg" p="md">
      <YStack gapY="xxl">
        <YStack alignItems="center" gapY="xxl">
          <Icon className="text-textPrimary" name="IconCircleCheckFilled" size={64} />
          <Text type="h1">Payment confirmed!</Text>
        </YStack>

        <CardContainer>
          <Text mb="md" type="h5">
            Payment method details
          </Text>
          {firstPurchaseData?.selectedPurchaseType === 'Subscription' ? (
            <YStack gapY="md">
              <XStack alignItems="center" bg="neutral50" justifyContent="between" p="md" radius="md">
                <XStack alignItems="center" gapX="sm">
                  <Icon name="IconGift" size={16} />
                  <Text type="p-md">{firstPurchaseData.selectedPlanData?.label || 'Subscription Plan'}</Text>
                </XStack>
                <Text type="p-md">
                  {firstPurchaseData.selectedPlanData
                    ? getPlanSubtitle(firstPurchaseData.selectedPlanData, pricingTerminology)
                    : ''}
                </Text>
              </XStack>
              <YStack>
                {selectedPlanPricing?.primaryPrice ? (
                  <>
                    <PaymentSuccessLineItem
                      label="Paid today"
                      value={formatCurrencyNatural(selectedPlanPricing.primaryPrice)}
                    />
                    <Divider />
                  </>
                ) : null}
                {promotionTranslation.nextBillingDate ? (
                  <>
                    <PaymentSuccessLineItem
                      label="Renewal date"
                      value={dateFormatters['LL'](promotionTranslation.nextBillingDate)}
                    />
                    <Divider />
                  </>
                ) : null}
                <PaymentSuccessLineItem
                  label="Subscription plan"
                  value={`${firstPurchaseData.selectedPlanData?.label || 'Plan'} ${formatCurrencyNatural(selectedPlanPricing?.primaryPrice ?? firstPurchaseData.selectedPlanData?.price ?? makeCent(0))}/mo`}
                />
                <Divider />
                <PaymentSuccessLineItem label="Payment method" value="Credit card" />
              </YStack>
            </YStack>
          ) : firstPurchaseData?.selectedPurchaseType === 'ALaCarteCredits' ? (
            <YStack gapY="md">
              <XStack alignItems="center" bg="neutral50" justifyContent="between" p="sm" radius="md">
                <XStack alignItems="center" gapX="sm">
                  <Icon name="IconShoppingBag" size={20} />
                  <Text type="p-md">
                    {showCredits
                      ? `${firstPurchaseData.selectedCreditsPackage?.amount || 0} ${pricingTerminology.unit_term_plural} pack`
                      : 'Purchase details'}
                  </Text>
                </XStack>
              </XStack>
              <YStack>
                <PaymentSuccessLineItem
                  label="Paid today"
                  value={formatCurrencyNatural(firstPurchaseData.selectedCreditsPackage?.priceCent ?? makeCent(0))}
                />
                <Divider />
                <PaymentSuccessLineItem
                  label={`Price per ${pricingTerminology.unit_term}`}
                  value={formatCurrencyNatural(
                    makeCent(
                      Math.round(
                        (firstPurchaseData.selectedCreditsPackage?.priceCent ?? 0) /
                          (firstPurchaseData.selectedCreditsPackage?.amount ?? 1),
                      ),
                    ),
                  )}
                />
                <Divider />
                <PaymentSuccessLineItem label="Payment method" value="Credit card" />
              </YStack>
            </YStack>
          ) : (
            <YStack gapY="md">
              <PaymentSuccessLineItem label="Payment method" value="Credit card" />
              <PaymentSuccessLineItem label="Status" value="Payment confirmed" />
            </YStack>
          )}
        </CardContainer>
      </YStack>
    </YStack>
  );
};
